import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import { FontBody1 } from "../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import ResolveCapability from "../../../../components/resolve-capability/resolve-capability";
import { Input } from "../../../../components/inputs/input/input";
import { IOption } from "../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import { DROPDOWN_MENU_WIDTH, SelectDropdown } from "../../../../components/inputs/select-dropdown/select-dropdown";
import { LABEL_POSITIONS, Toggle } from "../../../../components/inputs/toggle/toggle";
import useDashboardTranslation from "../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { IValidationGroup } from "../../../../hooks/validation-group/validation-group-hook";
import { PROFILE_CAPABILITIES } from "../../../../models/ProfileCapabilities";
import { rootContainer, TYPE } from "../../../../services/container/root";
import CurrencyService from "../../../../services/currency-service";
import { AMOUNT_TYPE, FEE_TYPE } from "../../../policies/policy-types";
import CreateRmaSessionStore from "../../stores/create-rma-session-store";
import RmaFeeStore from "../../stores/rma-fee-store";
import {
	applyCreateRmaLabelFeeCapabilityDefaults,
	getCreateRmaLabelFeeCapabilityEnforcement,
} from "../../utils/label-fee-capability";

interface IProps {
	store: RmaFeeStore;
	sessionStore: CreateRmaSessionStore;
	validationGroup: IValidationGroup;
	isPolicyLedMode: boolean;
}

const RmaLabelFee: React.FC<IProps> = observer(({ store, sessionStore, validationGroup, isPolicyLedMode }) => {
	const currencyService = rootContainer.get<CurrencyService>(TYPE.CurrencyService);
	const { getTranslation } = useDashboardTranslation();
	const scope = "dashboard-create-rma";

	const feeAmountTypeOptions = sessionStore.feeAmountTypeOptions(store.feeType, getTranslation, scope);
	const shippingLabelFeesOptions = sessionStore.shippingLabelFeesOptions(getTranslation, scope);

	/**
	 * Recomputed as the selection changes: a label-cost fee type is only offered while every
	 * fulfillment in the session supports it.
	 */
	const isLabelCostSupported = sessionStore.isLabelCostSupported();
	const capabilityEnforcement = useMemo(
		() => getCreateRmaLabelFeeCapabilityEnforcement(isLabelCostSupported),
		[isLabelCostSupported],
	);

	useEffect(() => {
		applyCreateRmaLabelFeeCapabilityDefaults(
			{
				isCharged: store.isCharged,
				feeType: store.feeType,
				isPolicyLedMode,
			},
			{
				setCharged: store.setCharged,
				setFeeType: store.setFeeType,
			},
			capabilityEnforcement,
		);
	}, [
		store.isCharged,
		store.feeType,
		isPolicyLedMode,
		capabilityEnforcement,
		store,
	]);

	const isChargeToggleDisabled =
		sessionStore.hasNoShipment() ||
		isPolicyLedMode ||
		capabilityEnforcement.isChargeToggleDisabled;

	return (
		<>
			<div className={"line"}>
				<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
					{getTranslation("LABEL_FEE", scope)}
				</FontBody1>
				<Toggle
					label=""
					labelPosition={LABEL_POSITIONS.LEFT}
					checked={store.isCharged}
					onChange={store.setCharged}
					disabled={isChargeToggleDisabled}
				/>
			</div>
			{store.isCharged && (
				<div className={"indent"}>
					<div className={"opened-editor"}>
						<SelectDropdown
							options={shippingLabelFeesOptions as IOption[]}
							onChange={(v) => store.setFeeType(v?.value as FEE_TYPE)}
							menuWidth={DROPDOWN_MENU_WIDTH.MAX_CONTENT}
							value={shippingLabelFeesOptions.find((item) => item.value === store.feeType)}
							required={true}
							validationGroup={validationGroup}
							disabled={isPolicyLedMode || capabilityEnforcement.isTypeEnforced}
						/>
						{store.showAmountOptions && (
							<>
								{store.hasEditableAmount ? (
									<SelectDropdown
										value={feeAmountTypeOptions.find((o) => o.value === store.amountType)}
										options={feeAmountTypeOptions}
										menuWidth={DROPDOWN_MENU_WIDTH.MAX_CONTENT}
										onChange={(v) => v?.value && store.setAmountType(v.value as AMOUNT_TYPE)}
										required
										validationGroup={validationGroup}
										disabled={isPolicyLedMode}
									/>
								) : (
									<FontBody2>{getTranslation("FIXED_AMOUNT", scope)}</FontBody2>
								)}

								{!store.hasPercentageAmount &&
									sessionStore.paymentCurrencyCode &&
									currencyService.getCurrencySymbol(sessionStore.paymentCurrencyCode)}
								<Input
									value={store.amount}
									onChange={store.setAmount}
									type={"number"}
									min={0}
									required
									validationGroup={validationGroup}
									disabled={isPolicyLedMode}
								/>
								{store.hasPercentageAmount && "%"}
							</>
						)}
						<ResolveCapability capability={PROFILE_CAPABILITIES.FEE_TAXES_CALCULATION}>
							{({ isSupported }) =>
								isSupported && (
									<div className={"align-to-left"}>
										<Toggle
											label={getTranslation("CHARGE_TAX", scope)}
											checked={store.isChargeTax}
											onChange={store.setIsChargeTax}
											labelPosition={LABEL_POSITIONS.LEFT}
											disabled={isPolicyLedMode}
										/>
									</div>
								)
							}
						</ResolveCapability>
					</div>
				</div>
			)}
		</>
	);
});

export default RmaLabelFee;
